import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as React from "react";
import { cx } from "../internal/cx.js";

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  value?: number;
}

export interface CircularProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string;
  size?: number;
  strokeWidth?: number;
  value?: number;
}

function clamp(value: number | undefined): number {
  if (value === undefined || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/**
 * Linear progress bar (0–100). Radix gives the root `role="progressbar"`
 * but no accessible name; we default `aria-label="Progress"` so axe's
 * `aria-progressbar-name` rule passes. Consumers should override with a
 * domain-specific label whenever possible.
 */
export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(function Progress({ className, value = 0, ...props }, ref) {
  const safe = clamp(value);
  return (
    <ProgressPrimitive.Root
      ref={ref}
      aria-label="Progress"
      className={cx("cw-v2-progress", className)}
      value={safe}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="cw-v2-progress__indicator"
        style={{ transform: `translateX(-${100 - safe}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = "Progress";

/**
 * Circular variant. Hand-rolled (no Radix equivalent) for the SVG. Uses
 * an explicit `role="progressbar"` plus `aria-valuemin`/`max`/`now` so
 * screen readers announce the current percent.
 */
export function CircularProgress({
  className,
  label,
  size = 40,
  strokeWidth = 4,
  value = 0,
  ...props
}: CircularProgressProps) {
  const safe = clamp(value);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (safe / 100) * circumference;

  return (
    // biome-ignore lint/a11y/useFocusableInteractive: progressbars are a status role, not a tab-stop; ARIA explicitly does not require focus.
    <div
      aria-label={label ?? "Progress"}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={safe}
      className={cx("cw-v2-progress-circle", className)}
      role="progressbar"
      style={{ height: size, width: size }}
      {...props}
    >
      <svg aria-hidden="true" height={size} viewBox={`0 0 ${size} ${size}`} width={size}>
        <title>{label ?? "Progress"}</title>
        <circle
          className="cw-v2-progress-circle__track"
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="cw-v2-progress-circle__indicator"
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          strokeWidth={strokeWidth}
        />
      </svg>
    </div>
  );
}
